import * as _ from "lodash";
import * as React from "react";
import * as H from "history";
import { match } from "react-router";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { Table } from "src/core/dashboard/components/widgets";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import {
  IUser,
  UpdateUserParams,
  useUserApi
} from "src/core/users/api/users.api";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { getUserAssignedDropsTableColumns } from "src/core/shared/components/tables/userAssignedDropsTableColumns";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";

import {
  DropAssignmentTabs,
  UrlParams
} from "./CustomerUserDropAssignmentPage";
import DefaultBranch from "../DefaultBranch";

type TabProps = {
  customerId: number;
  user: IUser;
  history: H.History;
  terminology: ITerminology;
  match: match<UrlParams> | null;
  reloadUser: () => void;
  // Callback returns the desired tab to make active
  updateActiveTab: (name: DropAssignmentTabs) => void;
};

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "branchNumber", "DESC" ] ]
};

export type SelectableDrop = IDrop & {selected: boolean};

/**
 * Drops assigned to user, displayed in a table with default branch
 *
 * @returns
 */
const CustomerUserAssignedDropsTab: React.ReactFCWithChildren<TabProps> = props => {
  const {
    terminology, user, history, reloadUser, updateActiveTab
  } = props;

  const TableRef = React.useRef<TableRefProps>(null);
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const [ dropsResponse, getDrops ] = useDropsApi("GET_USER_DROPS");
  const [ reloadUserFlag, setReloadUserFlag ] = React.useState(false);
  const [ availableDrops, setAvailableDrops ] = React.useState<SelectableDrop[]>([]);
  const [ loading, setLoading ] = React.useState(false);
  const [ showTable, setShowTable ] = React.useState(true);
  const [ isFiltering, setIsFiltering ] = React.useState(false);

  //loading is set true by buttons too, this is so there is an instant reaction to interactions.
  React.useEffect(() => {
    setLoading(dropsResponse.pending);
  }, [ dropsResponse ]);

  React.useEffect(() => {
    const filtersToCheck = { ...dropsResponse.params?.filters };

    delete filtersToCheck.id;

    if (!_.isEmpty(filtersToCheck)) {
      setIsFiltering(true);
    } else {
      setIsFiltering(false);
    }
  }, [ dropsResponse.params?.filters ]);

  //getting the drops out of the response and mapping them to have a selected property
  React.useEffect(() => {
    setAvailableDrops(() => {
      const drops = dropsResponse.data?.result ? dropsResponse.data.result as SelectableDrop[] : [];

      return drops.map(drop => ({
        ...drop,
        selected: false
      }));
    });
  }, [ dropsResponse.data?.result ]);

  //determine if we should show the table if no data is present.
  React.useEffect(() => {
    if (dropsResponse.data?.result && dropsResponse.data?.result.length < 1 && !isFiltering) {
      setShowTable(false);
    }
  }, [ dropsResponse.data?.result, isFiltering ]);

  //if any errors
  React.useEffect(() => {
    if (updateUserResponse.error) {
      toast.error(`Could not save ${terminology.user.singular.text}. Please try again.`);
    }

    if (dropsResponse.error) {
      toast.error(`Could not retrieve ${terminology.drop.plural.text}. Please try again`);
    }
  }, [
    terminology,
    updateUserResponse.error,
    dropsResponse.error
  ]);

  // this is used to trigger the reloading of the user in the parent component, there user data comes from there.
  React.useEffect(() => {
    if (updateUserResponse.complete) {
      if (reloadUserFlag) {
        reloadUser();
        setReloadUserFlag(false);
      }
    }
    //only want to trigger this when the response is updated not the flag
  }, [ reloadUser, updateUserResponse ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IDrop>, pagination: PaginatedParams<IDrop>}) => {
      const filters = { ...params.filters };

      //we dont want the default branch in the table data
      filters.id = { $not: user.defaultBranchId };

      getDrops({
        userId: user.id,
        pagination: params.pagination,
        filters
      });
    },
    [ user, getDrops ]
  );

  //handles sending the multiple restrict request.
  const handleBulkRestrict = React.useCallback(() => {
    setAvailableDrops(prevDrops => {
      if (!user || !user.userBranches) {
        return prevDrops;
      }

      if (prevDrops.length < 1) {
        toast.error(`Please select at least 1 ${terminology.drop.singular.text}`);

        return prevDrops;
      }

      const selectedDrops = prevDrops.filter(drop => drop.selected);
      const selectedDropIds = selectedDrops.map(drop => drop.id);
      const newDrops = user.userBranches.filter(drop => !selectedDropIds.includes(drop.id));

      updateUser({
        ...user,
        userBranches: newDrops
      } as UpdateUserParams);
      setReloadUserFlag(true);
      setLoading(true);

      //reset selected drops
      return [];
    });
  }, [
    user,
    updateUser,
    terminology
  ]);

  //for use in singular unassignment
  const unAssignDrop = React.useCallback((dropId: number) => {
    if (!user || !user.userBranches) {
      return;
    }
    const userBranches = user.userBranches;
    const toRemoveI = userBranches.findIndex(drop => drop.id === dropId);

    if (toRemoveI > -1) {
      userBranches.splice(toRemoveI, 1);
    }

    setLoading(true);

    updateUser({
      ...user,
      userBranches
    } as UpdateUserParams);
    setReloadUserFlag(true);
  }, [
    setReloadUserFlag,
    user,
    updateUser
  ]);

  const setDefaultDrop = React.useCallback((dropId: number) => {
    updateUser({
      ...user,
      //do not set the user branches
      userBranches: [],
      defaultBranchId: dropId
    } as UpdateUserParams);
    setReloadUserFlag(true);
  },
  [
    setReloadUserFlag,
    updateUser,
    user
  ]
  );

  const columns = React.useMemo(() => {
    let isDropSelected = false;

    return getUserAssignedDropsTableColumns({
      //action button per row.
      actionButton: info => {
        return (
          <ActionButton
            title="Actions"
            items={[
              {
                title: "View",
                action: () => {
                  history.push(`/admin/customers/${info.row.original.customerId}/drops/${info.row.original.id}/orders`);
                },
                newTabPath: `/admin/customers/${info.row.original.customerId}/drops/${info.row.original.id}/orders`
              },
              {
                title: "Set As Default",
                action: () => {
                  setDefaultDrop(info.row.original.id);
                  setLoading(true);
                }
              },
              {
                title: "Restrict Access",
                icon: Icons.WARN,
                action: () => {
                  unAssignDrop(info.row.original.id);
                }
              }
            ]}
          />
        );
      },
      //handler for the check box in each row
      checkboxAction: (drop: IDrop) => {
        setAvailableDrops(prevDrops => {
          const dropExists = prevDrops.findIndex(d => drop.id === d.id);
          const editedDrops = [ ...prevDrops ];

          editedDrops[ dropExists ].selected = !editedDrops[ dropExists ].selected;
          isDropSelected = editedDrops.filter(drop => drop.selected).length ? true : false;

          return editedDrops;
        });
      },
      bulkActionButton: () => {
        return (
          <ActionButton
            title="Bulk Actions"
            items={[
              {
                title: "Restrict Access",
                icon: Icons.WARN,
                disabled: !isDropSelected,
                action: handleBulkRestrict
              },
              {
                title: "Select All",
                action: () => {
                  setAvailableDrops(prevDrops => {
                    const selectedDrops = prevDrops.filter(drop => drop.selected === true);

                    if (selectedDrops.length === prevDrops.length) {
                      isDropSelected = false;

                      return prevDrops.map(drop => ({
                        ...drop,
                        selected: false
                      }));
                    }

                    isDropSelected = true;

                    return prevDrops.map(drop => ({
                      ...drop,
                      selected: true
                    }));
                  });
                }
              }
            ]}
          />
        );
      },
      terminology

    });
  }, [
    handleBulkRestrict,
    terminology,
    history,
    setDefaultDrop,
    unAssignDrop
  ]);

  return (
    <AssignedDropsTabWrapper>
      <DefaultBranch
        branchId={user.defaultBranchId}
        terminology={terminology}
      />

      {showTable ? (
        <>
          <Table
            ref={TableRef}
            tableOf={`${user.firstName} can also access the following ${terminology.drop.plural.text}`}
            error={dropsResponse.error}
            loading={loading}
            data={availableDrops && !loading ? availableDrops : []}
            onFetchData={onFetchData}
            defaultPagination={defaultPagination}
            pageCount={dropsResponse.data?.pages ? dropsResponse.data.pages : 0}
            rowCount={dropsResponse.data?.count ? dropsResponse.data.count : 0}
            columns={columns}
          />
        </>
      ) : (
        <div className="give-access">
          <Text as="h3">

            {` There are no other accessible ${terminology.drop.plural.text}  `}

          </Text>

          <Button
            onClick={() => {
              // Make the restricted tab active
              updateActiveTab(DropAssignmentTabs.Restricted);
            }}
          >
            Give Access
          </Button>
        </div>
      )}

    </AssignedDropsTabWrapper>
  );
};

const AssignedDropsTabWrapper = styled.div`
  ${({ theme }) => css`
    .give-access {
      display: flex;
      flex-direction: row;

      align-items: center;
      
      h3 {
      padding:${theme.spacing.padding.md};      
    }
    
    h3{
      margin-bottom: 0px
    }
  `}
`;

export default CustomerUserAssignedDropsTab;
